import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import './d28d-consumer-challenges.css';

const PHOTO_SLOTS = [
  { slot: 'frente', label: 'Frente' },
  { slot: 'lateral', label: 'Lateral derecha' },
  { slot: 'espalda', label: 'Espalda' },
  { slot: 'detalle', label: 'Lateral izquierdo' },
];

const emptyMeasures = () => ({
  peso_kg: '',
  grasa_pct: '',
  cintura_cm: '',
  cadera_cm: '',
  brazo_cm: '',
  notas: '',
});

export default function D28dChallengeWizard({ challengeId, onComplete }) {
  const { t } = useI18n();
  const [eligibility, setEligibility] = useState(null);
  const [measures, setMeasures] = useState(emptyMeasures);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/d28d/challenges/${challengeId}/eligibility`);
      setEligibility(r.data?.data || null);
    } catch {
      setEligibility(null);
    }
  }, [challengeId]);

  useEffect(() => { load(); }, [load]);

  const uploadPhoto = async (slot, file) => {
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', 'image');
      fd.append('slot', slot);
      await api.post(`/d28d/challenges/${challengeId}/evidence`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg(t('d28d.wizard.photo_ok', 'Foto guardada'));
      await load();
      onComplete?.();
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.wizard.photo_err', 'No se pudo subir la foto'));
    } finally {
      setBusy(false);
    }
  };

  const saveMeasures = async () => {
    setBusy(true);
    setError('');
    try {
      const contenido = JSON.stringify({
        ...measures,
        peso_kg: measures.peso_kg ? Number(measures.peso_kg) : null,
        grasa_pct: measures.grasa_pct ? Number(measures.grasa_pct) : null,
        cintura_cm: measures.cintura_cm ? Number(measures.cintura_cm) : null,
        cadera_cm: measures.cadera_cm ? Number(measures.cadera_cm) : null,
        brazo_cm: measures.brazo_cm ? Number(measures.brazo_cm) : null,
      });
      await api.post(`/d28d/challenges/${challengeId}/evidence`, { tipo: 'measures', contenido });
      setMsg(t('d28d.wizard.measures_ok', 'Medidas guardadas'));
      await load();
      onComplete?.();
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.wizard.measures_err', 'No se pudieron guardar las medidas'));
    } finally {
      setBusy(false);
    }
  };

  if (!eligibility) {
    return <p className="text-sm d28d-text-muted">{t('d28d.wizard.loading', 'Cargando requisitos…')}</p>;
  }

  const checklist = eligibility.checklist || {};
  const minClasses = eligibility.min_classes ?? 8;
  const attended = eligibility.classes_attended ?? 0;

  return (
    <div className="d28d-challenge-wizard space-y-4">
      <h4 className="font-semibold text-base m-0">
        {t('d28d.wizard.title', 'Completa tu postulación')}
      </h4>

      <div className={`flex items-center gap-2 text-sm ${eligibility.attendance_ok ? 'd28d-challenge-wizard__ok' : 'd28d-challenge-wizard__pending'}`}>
        {eligibility.attendance_ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
        <span>
          {t('d28d.wizard.attendance', 'Asistencia')}: {attended}/{minClasses} {t('d28d.wizard.classes', 'clases')}
        </span>
      </div>

      <fieldset className="space-y-2 border-0 p-0 m-0">
        <legend className="text-sm font-medium px-0">
          {t('d28d.wizard.measures', 'Pruebas físicas')}
        </legend>
        <div className="grid grid-cols-2 gap-2">
          <input className="ui-input" placeholder="Peso (kg)" value={measures.peso_kg} onChange={(e) => setMeasures({ ...measures, peso_kg: e.target.value })} />
          <input className="ui-input" placeholder="% grasa" value={measures.grasa_pct} onChange={(e) => setMeasures({ ...measures, grasa_pct: e.target.value })} />
          <input className="ui-input" placeholder="Cintura (cm)" value={measures.cintura_cm} onChange={(e) => setMeasures({ ...measures, cintura_cm: e.target.value })} />
          <input className="ui-input" placeholder="Cadera (cm)" value={measures.cadera_cm} onChange={(e) => setMeasures({ ...measures, cadera_cm: e.target.value })} />
          <input className="ui-input col-span-2" placeholder="Brazo (cm)" value={measures.brazo_cm} onChange={(e) => setMeasures({ ...measures, brazo_cm: e.target.value })} />
          <textarea className="ui-input col-span-2" rows={2} placeholder="Notas" value={measures.notas} onChange={(e) => setMeasures({ ...measures, notas: e.target.value })} />
        </div>
        <button type="button" className="btn-primary text-sm" disabled={busy} onClick={saveMeasures}>
          {checklist.measures_ok ? t('d28d.wizard.measures_update', 'Actualizar medidas') : t('d28d.wizard.measures_save', 'Guardar medidas')}
        </button>
      </fieldset>

      <fieldset className="space-y-2 border-0 p-0 m-0">
        <legend className="text-sm font-medium px-0">
          {t('d28d.wizard.photos', '4 fotos (con fecha visible)')}
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(checklist.slots || PHOTO_SLOTS.map((p) => ({ slot: p.slot, filled: false }))).map(({ slot, filled }) => {
            const meta = PHOTO_SLOTS.find((p) => p.slot === slot) || { label: slot };
            return (
              <label
                key={slot}
                className={`d28d-challenge-wizard__slot ${filled ? 'd28d-challenge-wizard__slot--filled' : ''}`}
              >
                <span>{meta.label}{filled ? ' ✓' : ''}</span>
                <span className="inline-flex items-center gap-1 text-xs d28d-text-muted">
                  <Upload className="w-3 h-3" aria-hidden />
                  Subir
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    disabled={busy}
                    onChange={(e) => e.target.files[0] && uploadPhoto(slot, e.target.files[0])}
                  />
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {eligibility.eligible ? (
        <p className="text-sm font-semibold d28d-challenge-wizard__ok flex items-center gap-2 m-0">
          <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden />
          {t('d28d.wizard.eligible', '¡Postulación completa! Quedas inscrito para la premiación.')}
        </p>
      ) : (
        <p className="text-sm d28d-challenge-wizard__pending m-0">
          {t('d28d.wizard.pending', 'Completa medidas, fotos y asistencia mínima para quedar postulado.')}
        </p>
      )}

      {eligibility.instagram_live_url ? (
        <a
          href={eligibility.instagram_live_url}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-[var(--shell-primary,var(--d28d-yellow,#ffd700))] underline"
        >
          {t('d28d.wizard.instagram', 'Ver Instagram Live de premiación')}
        </a>
      ) : null}

      {msg ? <p className="text-sm d28d-challenge-wizard__ok m-0">{msg}</p> : null}
      {error ? <p className="text-sm text-red-400 m-0">{error}</p> : null}
    </div>
  );
}
